import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, getDocs, Timestamp, doc, getDoc, limit } from 'firebase/firestore';
import { Input, Avatar, Badge, Tooltip, Button } from 'antd';
import { SendOutlined, UserOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { formatRoleDisplay, getRoleColor } from '../models/role';
import { createMessageAuthorName, CurrentUserDisplay, MessageAuthorName, TypingIndicator } from '../utils/userDisplayUtils';
import { createIncidentLog, ACTIVITY_TYPES, getUserFullName } from '../utils/activityLogger';

const { TextArea } = Input;

const LiveChatPanel = ({ incidentId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const { currentUser } = useAuth();
  const { db } = useFirestore();

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Effect to scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Effect to scroll to bottom only when new messages are added
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    
    if (messages.length > prevMessagesLengthRef.current) {
      scrollToBottom();
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages]);

  // Convert Firestore timestamp to Date
  const convertTimestamp = (timestamp) => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    return new Date(timestamp);
  };

  // Process message data
  const processMessageData = (doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      timestamp: data.timestamp?.toDate() || new Date(),
      message: data.message || '',
      authorName: data.authorName || 'Unknown User',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      role: data.role || 'user'
    };
  };

  // Setup real-time listener
  const setupListener = async () => {
    if (!incidentId || !currentUser) {
      console.log('Cannot setup listener - missing required data:', { 
        hasIncidentId: !!incidentId, 
        hasUser: !!currentUser,
        currentUser: currentUser
      });
      return;
    }

    // Debug logging
    console.log('DEBUG - Current User:', {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName,
      role: currentUser.role,
      orgId: currentUser.orgId,
      isAdmin: currentUser.isAdmin,
      isSuperAdmin: currentUser.isSuperAdmin,
      isLoaded: currentUser.isLoaded,
      isAuthenticated: !!currentUser.uid,
      rawUserData: currentUser
    });

    try {
      // Get user document
      const userRef = doc(db, 'c3_users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.error('DEBUG - User document does not exist:', currentUser.uid);
        setIsLoading(false);
        return;
      }

      const userData = userSnap.data();
      console.log('DEBUG - User document data:', userData);

      // Get incident document
      const incidentRef = doc(db, 'c3_incidents', incidentId);
      const incidentSnap = await getDoc(incidentRef);
      
      if (!incidentSnap.exists()) {
        console.error('DEBUG - Incident document does not exist:', incidentId);
        setIsLoading(false);
        return;
      }

      const incidentData = incidentSnap.data();
      console.log('DEBUG - Incident document data:', incidentData);

      // Create query with orgId filter for security
      const q = query(
        collection(db, 'c3_incidentChats'),
        where('incidentId', '==', incidentId),
        where('orgId', '==', incidentData.orgId),
        orderBy('timestamp', 'asc'),
        limit(100)
      );

      // Cleanup previous listener if exists
      if (unsubscribeRef.current) {
        console.log('Cleaning up previous listener');
        unsubscribeRef.current();
      }

      unsubscribeRef.current = onSnapshot(q, 
        (snapshot) => {
          console.log('Received snapshot with', snapshot.docs.length, 'messages');
          const newMessages = snapshot.docs.map(processMessageData);
          setMessages(newMessages);
          setIsLoading(false);
        },
        (error) => {
          console.error('Error in real-time listener:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            stack: error.stack,
            currentUser: {
              uid: currentUser.uid,
              role: currentUser.role,
              orgId: currentUser.orgId,
              isSuperAdmin: currentUser.isSuperAdmin,
              isAuthenticated: !!currentUser.uid,
              userData,
              incidentData,
              rawUserData: currentUser
            }
          });
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Error setting up listener:', error);
      setIsLoading(false);
    }
  };

  // Load initial messages and setup listener
  useEffect(() => {
    setIsLoading(true);
    setupListener();

    return () => {
      if (unsubscribeRef.current) {
        console.log('Cleaning up listener on unmount');
        unsubscribeRef.current();
      }
    };
  }, [incidentId, currentUser?.uid]);

  // Handle message submission
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !incidentId || !currentUser) {
      console.log('Cannot send message - missing required data:', { 
        hasMessage: !!newMessage.trim(), 
        hasIncidentId: !!incidentId, 
        hasUser: !!currentUser 
      });
      return;
    }

    // Verify authentication
    if (!currentUser.uid) {
      console.error('User is not authenticated');
      alert('You must be logged in to send messages');
      return;
    }

    try {
      // Get user document
      const userRef = doc(db, 'c3_users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        console.error('User document does not exist:', currentUser.uid);
        alert('Your user account is not properly set up. Please contact support.');
        return;
      }

      const userData = userSnap.data();
      console.log('User data:', userData);

      // Get incident data
      const incidentRef = doc(db, 'c3_incidents', incidentId);
      const incidentSnap = await getDoc(incidentRef);
      
      if (!incidentSnap.exists()) {
        console.error('Incident not found:', incidentId);
        alert('Incident not found');
        return;
      }

      const incidentData = incidentSnap.data();
      
      // Verify user has access to this incident
      if (!currentUser.isSuperAdmin && 
          !userData.isAdmin && 
          incidentData.orgId !== userData.orgId) {
        console.error('User does not have access to this incident:', {
          userOrgId: userData.orgId,
          incidentOrgId: incidentData.orgId
        });
        alert('You do not have access to this incident');
        return;
      }

      // Create proper display name using universal utility
      const authorDisplayName = createMessageAuthorName(userData);
      console.log('Message creation using universal utility:', authorDisplayName);

      // Add message to chat
      console.log('Attempting to add message to c3_incidentChats:', {
        incidentId,
        message: newMessage.trim(),
        authorId: currentUser.uid,
        authorName: authorDisplayName,
        orgId: incidentData.orgId
      });

      const docRef = await addDoc(collection(db, 'c3_incidentChats'), {
        incidentId,
        message: newMessage.trim(),
        timestamp: serverTimestamp(),
        authorId: currentUser.uid,
        authorName: authorDisplayName,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        role: userData.role || 'user',
        orgId: incidentData.orgId // Add orgId to chat message
      });

      console.log('Message successfully added with ID:', docRef.id);

      // FORENSIC LOGGING: Log live chat message creation
      try {
        const userFullName = await getUserFullName(db, currentUser.uid, currentUser.email, userData);
        await createIncidentLog({
          incidentId,
          orgId: incidentData.orgId,
          action: ACTIVITY_TYPES.LIVE_CHAT_MESSAGE_CREATED,
          details: `Live chat message sent: "${newMessage.trim().substring(0, 100)}${newMessage.trim().length > 100 ? '...' : ''}"`,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          userFullName,
          priority: 'low',
          metadata: {
            messageId: docRef.id,
            messageLength: newMessage.trim().length,
            userData
          }
        });
      } catch (logError) {
        console.error('Error logging live chat message creation:', logError);
        // Don't fail message creation if logging fails
      }

      setNewMessage('');
      setIsTyping(false);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  // Handle typing indicator
  const handleTyping = () => {
    setIsTyping(true);
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    setTypingTimeout(setTimeout(() => setIsTyping(false), 2000));
  };

  // Handle input change
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    handleTyping();
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = convertTimestamp(timestamp);
    return format(date, 'h:mma, d MMM');
  };

  // Get full name with improved logic
  const getFullName = (message) => {
    // Prioritize firstName + lastName
    if (message.firstName && message.lastName) {
      return `${message.firstName.trim()} ${message.lastName.trim()}`;
    }
    // Use just firstName if available
    if (message.firstName && message.firstName.trim()) {
      return message.firstName.trim();
    }
    // If authorName looks like an email, extract username
    if (message.authorName && message.authorName.includes('@')) {
      return message.authorName.split('@')[0];
    }
    // Use authorName as-is if it doesn't look like an email
    return message.authorName || 'Unknown User';
  };

  return (
    <div className="live-chat-panel">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold m-0">Live Updates</h3>
          <Tooltip 
            title="Text typed in Live Updates will be visible to all CMT members and will be used by AI to inform crisis summaries"
            placement="topLeft"
            overlayStyle={{ maxWidth: '300px' }}
          >
            <InfoCircleOutlined 
              style={{ 
                color: '#1890ff', 
                fontSize: '14px',
                cursor: 'help'
              }} 
            />
          </Tooltip>
        </div>
      </div>

      {/* Messages Container */}
      <div className="messages-container" ref={messagesContainerRef}>
        {isLoading ? (
          <div className="text-center py-4">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No messages yet</div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`message mb-2 ${message.isSystemMessage ? 'system-message' : ''}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <MessageAuthorName message={message} db={db} />
                    <Badge 
                      count={message.isSystemMessage ? 'SYSTEM' : formatRoleDisplay(message.role, message.cmtRole)} 
                      style={{ 
                        backgroundColor: message.isSystemMessage ? '#ff4d4f' : getRoleColor(message.role, message.cmtRole),
                        fontSize: '10px',
                        padding: '0 6px'
                      }}
                    />
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(message.timestamp)}
                    </span>
                  </div>
                  <div className={`px-3 pt-3 pb-1.5 rounded-lg border shadow ${
                    message.isSystemMessage 
                      ? 'bg-red-50 border-red-200 shadow-red-100' 
                      : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-sm whitespace-pre-wrap ${
                      message.isSystemMessage ? 'text-red-800' : 'text-gray-800'
                    }`}>
                      {(() => {
                        const urlRegex = /(https?:\/\/[^\s]+)/g;
                        const parts = String(message.message || '').split(urlRegex);
                        return parts.map((part, idx) => {
                          if (urlRegex.test(part)) {
                            return (
                              <a key={idx} href={part} target="_blank" rel="noopener noreferrer">
                                {part}
                              </a>
                            );
                          }
                          return <span key={idx}>{part}</span>;
                        });
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="input-container">
        <CurrentUserDisplay currentUser={currentUser} db={db} />
        <TextArea
          value={newMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
          autoSize={{ minRows: 2, maxRows: 4 }}
          className="mb-2"
        />
        <div className="flex justify-between items-center">
          {isTyping && (
            <TypingIndicator currentUser={currentUser} db={db} />
          )}
          <Button
            type="primary"
            onClick={handleSendMessage}
            icon={<SendOutlined />}
            style={{ 
              backgroundColor: '#39325b',
              borderColor: '#39325b',
              marginLeft: 'auto'
            }}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LiveChatPanel; 